// apps/admin/src/pages/CompanyDetailsPage.tsx - ENHANCED VERSION
// Full functional company details page with expandable ZIP file viewer

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useOutletContext } from 'react-router-dom';
import {
  doc, getDoc, collection, onSnapshot, query, where, getDocs,
  updateDoc, serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';
import {
  ChevronLeft, Edit2, Users, Building2, X,
  AlertCircle, TrendingUp, Activity, Trash2, Archive,
  ChevronDown, ChevronRight, Package, File, Loader,
  ShieldCheck,
} from 'lucide-react';
import OnboardingDataUploader from '../components/OnboardingDataUploader';
import EntorsUploadSection from '../components/EntorsUploadSection';
import EmlUploadSection from '../components/EmlUploadSection';
import PipelineTrackerUploadSection from '../components/PipelineTrackerUploadSection';
import { ZipUploadMonitor } from '../components/ZipUploadMonitor';
import RctiVerificationTab from '../components/RctiVerificationTab';
import UploadVerificationReport from '../components/UploadVerificationReport';
import AggregatorProfileCard from '../components/AggregatorProfileCard';
import { toast } from 'react-hot-toast';
import { companyDetailsChanges, companyDetailsFormFrom, type CompanyDetailsForm } from './companyDetailsChanges';
import { useAdminAuditLog } from '../hooks/useAdminAuditLog';
import { formatUploadDisplayName } from '@valutten/shared-utils';

// --- TYPES ---
type ThemeName = 'dashboard' | 'companies' | 'onboard' | 'analytics' | 'security';
type Theme = { from: string; to: string; glow: string; [key: string]: any; };
type OutletContextType = {
  activeTheme: ThemeName;
  themes: Record<ThemeName, Theme>;
};

interface Company {
  id: string;
  /** Aggregator profile key (aggregatorProfiles.ts); set via setCompanyAggregator. */
  aggregator?: unknown;
  featureFlags?: Record<string, unknown>;
  companyName?: string;
  name?: string;
  /** Registered entity name; the eDM sender identity needs it. */
  legalName?: string;
  abn?: string;
  acl?: string;
  email?: string;
  phone?: string;
  address?: string;
  state?: string;
  status?: string;
  brokerCount?: number;
  activeUsers?: number;
  monthlyRevenue?: number;
  createdAt?: any;
  updatedAt?: any;
  subscription?: {
    plan: 'monthly' | 'annual';
    amount: number;
    currency: string;
    status: string;
    startDate?: any;
    nextBillingDate?: any;
  };
}

// Per-file processing status from the commission_sources.processing map
interface FileProcessingStatus {
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'rejected_duplicate';
  error?: string;
  totalRows?: number;
  recordsWritten?: number;
  skippedDuplicates?: number;
  supersededPrevious?: number;
  errors?: number;
  successRate?: number;
  hasWarning?: boolean;
  queuedAt?: { toDate: () => Date };
  startedAt?: { toDate: () => Date };
  completedAt?: { toDate: () => Date };
  // Logical-key dedup rejection (Gate B, mode=enforce) — duplicate of an already-ingested upload.
  rejectionReason?: string;
  supersedesUploadId?: string;
  detectedPeriodMonth?: string;
  detectedRunNumber?: number;
}

interface ZipUpload {
  id: string;
  fileName: string;
  displayName?: string;
  storagePath: string;
  status: 'processing' | 'queued' | 'completed' | 'completed_with_errors' | 'completed_with_rejections' | 'failed' | 'cancelled';
  filesRejected?: number;
  uploadedAt?: { toDate: () => Date };
  filesQueued?: number;
  filesTotal?: number;
  filesFailed?: number;
  filesSucceeded?: number;
  officeId?: string;
  // Embedded per-file processing status (authoritative source)
  processing?: Record<string, FileProcessingStatus>;
  // DEPRECATED: queuedFileIds - use processing map instead
  queuedFileIds?: string[];
}

interface Broker {
  id: string;
  displayName: string;
  email: string;
  role: string;
  status: string;
  lastActive?: any;
  officeIds?: string[];
}

interface Office {
  id: string;
  name: string;
  principalBrokerId?: string | null;
}

interface FileTemplate {
  id: string;
  name: string;
  templateName?: string;
  aggregator?: string;
}

/**
 * Module-scope cache for the GLOBAL Entors fileTemplate ID.
 *
 * `undefined`  → not yet checked (fetch on next page mount).
 * `null`       → confirmed not present in this Firestore project.
 * `string`     → the doc ID of the Entors template.
 *
 * Entors is a globally-registered fileTemplate, not a per-tenant config — its
 * presence determines whether the admin "Entors" upload tab is rendered for
 * ANY company. Without this cache, `getTemplates({})` fires on EVERY visit to
 * a CompanyDetailsPage, regardless of whether that tenant uses Entors. Cache
 * lives for the admin app session; survives client-side navigation between
 * subscriber detail pages.
 */
let cachedEntorsTemplateId: string | null | undefined = undefined;
let pendingEntorsTemplateFetch: Promise<string | null> | null = null;

const CompanyDetailsPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { logAction } = useAdminAuditLog();

  const contextData = useOutletContext<OutletContextType | undefined>();
  const activeTheme = contextData?.activeTheme ?? 'companies';
  const themes = contextData?.themes ?? {
    dashboard: { from: '#6366f1', to: '#8b5cf6', glow: 'shadow-indigo-500/30' },
    companies: { from: '#2563eb', to: '#3b82f6', glow: 'shadow-blue-500/30' },
    onboard: { from: '#16a34a', to: '#22c55e', glow: 'shadow-green-500/30' },
    analytics: { from: '#0891b2', to: '#06b6d4', glow: 'shadow-cyan-500/30' },
    security: { from: '#db2777', to: '#f472b6', glow: 'shadow-pink-500/30' }
  };

  // State
  const [company, setCompany] = useState<Company | null>(null);
  const [zipUploads, setZipUploads] = useState<ZipUpload[]>([]);
  const [expandedZips, setExpandedZips] = useState<Set<string>>(new Set());
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'files' | 'brokers' | 'offices' | 'entors' | 'verification' | 'upload-report' | 'email-upload' | 'pipeline-tracker'>('overview');
  const [offices, setOffices] = useState<Office[]>([]);
  const [entorsTemplateId, setEntorsTemplateId] = useState<string | null>(null);
  const [pipelineTrackerGranted, setPipelineTrackerGranted] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [accessModalOpen, setAccessModalOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'archive' | 'delete'>('archive');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteUsers, setDeleteUsers] = useState(true);

  // The Edit Company modal's fields, always strings: built with the same
  // coercion as the loaded baseline, so a legacy non-string value (e.g. a
  // numeric abn) can never reach .trim().
  const [companyData, setCompanyData] = useState<CompanyDetailsForm>(() => companyDetailsFormFrom({}));
  const [inviteData, setInviteData] = useState({
    email: '', role: 'broker', officeIds: [] as string[]
  });
  const [activeZipUpload, setActiveZipUpload] = useState<{
    companyId: string;
    sourceDocId: string;
  } | null>(null);
  const [cancellingUpload, setCancellingUpload] = useState<string | null>(null);
  const [activeUserCount, setActiveUserCount] = useState<number>(0);
  const [clientUploads, setClientUploads] = useState<any[]>([]);
  const [clientUploadsLoading, setClientUploadsLoading] = useState(false);
  const [subscriptionConfig, setSubscriptionConfig] = useState<any>(null);
  // Files tab: long upload lists default to collapsed so the upload zone and
  // each section header stay reachable without scrolling past 800+ rows.
  const [processedUploadsExpanded, setProcessedUploadsExpanded] = useState(false);
  const [adminZipsExpanded, setAdminZipsExpanded] = useState(false);

  // Fetch company and ZIP uploads
  useEffect(() => {
    if (!companyId) {
      setError('No company ID provided');
      setLoading(false);
      return;
    }

    const fetchCompany = async () => {
      try {
        const companyRef = doc(db, 'companies', companyId);
        const companySnap = await getDoc(companyRef);
        if (companySnap.exists()) {
          const data = { id: companySnap.id, ...companySnap.data() } as Company;
          setCompany(data);
          setCompanyData(companyDetailsFormFrom(data));

          void logAction({
            action: 'ADMIN_COMPANY_VIEWED',
            companyId,
            companyName: data.companyName || data.name || companyId,
            resource: `companies/${companyId}`,
            resourceName: data.companyName || data.name || companyId,
            metadata: { view: 'company_details' },
          });
        } else {
          setError('Company not found');
        }
      } catch (err) {
        setError('Failed to fetch company details');
      }
    };

    const fetchBrokers = async () => {
      if (!companyId) return;
      try {
        // Fetch from brokers subcollection (discovered from commission files)
        const brokersQuery = query(collection(db, `companies/${companyId}/brokers`));
        const snapshot = await getDocs(brokersQuery);
        setBrokers(snapshot.docs
          // Filter out merged brokers (they've been consolidated into another broker)
          .filter(d => !d.data().mergedInto)
          .map(d => {
            const data = d.data();
            return {
              id: d.id,
              // Brokers collection uses currentName, fallback to displayName for compatibility
              displayName: data.currentName || data.displayName || d.id,
              email: data.email || '',
              role: 'broker',
              status: data.status || 'active',
              officeIds: data.officeIds || []
            } as Broker;
          }));
      } catch (err) {
        console.error('Error fetching brokers:', err);
      }
    };

    // Fetch active users for this company
    const fetchUsers = async () => {
      if (!companyId) return;
      try {
        const usersQuery = query(collection(db, 'users'), where('companyId', '==', companyId));
        const snapshot = await getDocs(usersQuery);
        setActiveUserCount(snapshot.size);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };

    // Fetch subscription config
    const fetchSubscription = async () => {
      if (!companyId) return;
      try {
        const subDoc = await getDoc(doc(db, `companies/${companyId}/subscription/config`));
        if (subDoc.exists()) {
          setSubscriptionConfig(subDoc.data());
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
      }
    };

    // Fetch client uploads from processing queue (limit 500 to avoid cap)
    const fetchClientUploads = async () => {
      if (!companyId) return;
      setClientUploadsLoading(true);
      try {
        const listUploadsFn = httpsCallable(functions, 'listCompanyUploads');
        const result = await listUploadsFn({ companyId }) as any;
        if (result.data.success) {
          setClientUploads(result.data.uploads || []);
        }
      } catch (err) {
        console.error('Error fetching client uploads:', err);
      } finally {
        setClientUploadsLoading(false);
      }
    };

    void fetchCompany();
    void fetchBrokers();
    void fetchUsers();
    void fetchSubscription();
    void fetchClientUploads();

    // Fetch offices for the company
    const fetchOffices = async () => {
      if (!companyId) return;
      try {
        const officesQuery = query(collection(db, `companies/${companyId}/offices`));
        const snapshot = await getDocs(officesQuery);
        const fetchedOffices = snapshot.docs.map(d => ({
          id: d.id,
          name: d.data().name,
          principalBrokerId: d.data().principalBrokerId || null
        } as Office));
        setOffices(fetchedOffices);
      } catch (err) {
        console.error('Error fetching offices:', err);
      }
    };

    void fetchOffices();

    // Resolve the GLOBAL Entors fileTemplate ID (used to decide whether to
    // render the "Entors" upload tab — see line ~660 / EntorsUploadSection
    // mount). Module-scope cache means we hit `getTemplates({})` once per
    // admin session, not once per company-page navigation. The result is
    // tenant-independent so caching across companies is correct.
    const resolveEntorsTemplate = async () => {
      // Cache hit — use it (including the `null` sentinel meaning "checked,
      // not present"). Don't re-fetch.
      if (cachedEntorsTemplateId !== undefined) {
        setEntorsTemplateId(cachedEntorsTemplateId);
        return;
      }
      // Coalesce concurrent mounts into a single in-flight fetch.
      if (!pendingEntorsTemplateFetch) {
        pendingEntorsTemplateFetch = (async () => {
          try {
            const getTemplates = httpsCallable(functions, 'getTemplates');
            const result = (await getTemplates({})) as any;
            if (!result.data?.success) return null;
            const match = result.data.templates.find((t: FileTemplate) =>
              (t.name || t.templateName || '').toLowerCase().includes('entors')
            );
            return match?.id ?? null;
          } catch (err) {
            console.error('[CompanyDetails] resolveEntorsTemplate failed:', err);
            return null;
          }
        })();
      }
      const id = await pendingEntorsTemplateFetch;
      cachedEntorsTemplateId = id;
      pendingEntorsTemplateFetch = null;
      setEntorsTemplateId(id);
    };

    void resolveEntorsTemplate();

    // Listen to ZIP uploads from commission_sources
    const zipQuery = query(collection(db, `companies/${companyId}/commission_sources`));
    const unsubscribe = onSnapshot(zipQuery, (snapshot) => {
      const uploads = snapshot.docs.map(d => ({ id: d.id, ...d.data() } as ZipUpload));
      uploads.sort((a, b) =>
        (b.uploadedAt?.toDate()?.getTime() || 0) - (a.uploadedAt?.toDate()?.getTime() || 0)
      );
      setZipUploads(uploads);

      // Set activeZipUpload for currently processing uploads
      const processingUpload = uploads.find(u =>
        u.status === 'processing' || u.status === 'queued'
      );
      if (processingUpload) {
        setActiveZipUpload({
          companyId: companyId,
          sourceDocId: processingUpload.id
        });
      } else {
        setActiveZipUpload(null);
      }

      setLoading(false);
    }, () => {
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId, logAction]);

  // Toggle ZIP expansion - file data comes from embedded processing map, no query needed
  const toggleZipExpansion = (zipId: string) => {
    const newExpanded = new Set(expandedZips);
    if (expandedZips.has(zipId)) {
      newExpanded.delete(zipId);
    } else {
      newExpanded.add(zipId);
    }
    setExpandedZips(newExpanded);
  };

  // Pipeline Tracker add-on gate: the tab only exists for tenants that hold the
  // add-on. The real gate is `requireFeature` on the server callable.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'companies', companyId, 'entitlements', 'config'));
        const data = snap.exists() ? (snap.data() as any) : null;
        if (!cancelled) {
          setPipelineTrackerGranted(data?.addOnFeatures?.pipeline_tracker?.enabled === true);
        }
      } catch (err) {
        console.error('[CompanyDetails] pipeline tracker entitlement read failed:', err);
        if (!cancelled) setPipelineTrackerGranted(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // Observability: Log data inconsistencies for debugging
  useEffect(() => {
    zipUploads.forEach(zip => {
      if ((zip.filesQueued ?? 0) > 0 &&
          (!zip.processing || Object.keys(zip.processing).length === 0)) {
        console.warn(
          `[DATA_INCONSISTENCY] ZIP ${zip.id} has filesQueued=${zip.filesQueued} but empty processing map`,
          { zipId: zip.id, fileName: zip.fileName, status: zip.status }
        );
      }
    });
  }, [zipUploads]);

  // Handlers
  const handleSaveCompany = async () => {
    if (!companyId || !company) return;
    // Send ONLY the fields the founder changed ('' for a cleared one): the
    // callable deletes a cleared field and audits every field it is sent.
    const changes = companyDetailsChanges(companyData, companyDetailsFormFrom(company));
    if (changes.kind === 'none') {
      setEditModalOpen(false);
      return;
    }
    if (changes.kind === 'invalid') {
      toast.error(`❌ ${changes.message}`);
      return;
    }
    setSaveLoading(true);
    try {
      // The companies/{id} write rule has no founder arm, so the founder edits
      // through the audited callable.
      const updateCompanyDetails = httpsCallable<
        Record<string, string>,
        { success: boolean; updated: Record<string, string | null> }
      >(functions, 'updateCompanyDetails');
      const { data } = await updateCompanyDetails({ companyId, ...changes.fields });
      // Reflect what the server stored (ABN normalised to digits, blanks cleared).
      const stored = Object.fromEntries(Object.entries(data.updated).map(([k, v]) => [k, v ?? '']));
      toast.success('Company details updated successfully');
      setEditModalOpen(false);
      setCompanyData(prev => companyDetailsFormFrom({ ...prev, ...stored }));
      setCompany(prev => prev ? { ...prev, ...stored } : null);
    } catch (err: any) {
      toast.error(`❌ ${err?.message || 'Failed to update company details'}`, {
        duration: 8000,
        style: { background: '#991b1b', color: '#fff', fontSize: '14px', padding: '16px', fontWeight: 'bold', cursor: 'pointer' },
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!companyId || !inviteData.email) return;
    setInviteLoading(true);
    try {
      const createInvitation = httpsCallable(functions, 'createInvitation');
      await createInvitation({ companyId, ...inviteData });
      toast.success(`Invitation sent to ${inviteData.email}`);
      setInviteData({ email: '', role: 'broker', officeIds: [] });
    } catch (err: any) {
      toast.error(`❌ ${err.message || 'Failed to send invitation'}`, {
        duration: 8000,
        style: { background: '#991b1b', color: '#fff', fontSize: '14px', padding: '16px', fontWeight: 'bold', cursor: 'pointer' },
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleArchiveCompany = () => {
    setDeleteMode('archive');
    setDeleteModalOpen(true);
  };

  const handleDeleteCompany = () => {
    setDeleteMode('delete');
    setDeleteModalOpen(true);
  };

  const confirmDeleteAction = async () => {
    if (!companyId) return;

    if (deleteMode === 'delete' && deleteConfirmText !== 'DELETE') {
      toast.error('❌ Please type DELETE to confirm', {
        duration: 8000,
        style: { background: '#991b1b', color: '#fff', fontSize: '14px', padding: '16px', fontWeight: 'bold', cursor: 'pointer' },
      });
      return;
    }

    setDeleteLoading(true);

    try {
      if (deleteMode === 'archive') {
        const archiveFn = httpsCallable(functions, 'archiveCompany');
        await archiveFn({ companyId, archiveUsers: deleteUsers });
        toast.success('Company archived successfully');
      } else {
        const deleteFn = httpsCallable(functions, 'deleteCompany');
        await deleteFn({ companyId, confirmationText: 'DELETE', deleteUsers });
        toast.success('Company deleted permanently');
      }
      navigate('/companies');
    } catch (err: any) {
      toast.error(`❌ ${err.message || 'Operation failed'}`, {
        duration: 8000,
        style: { background: '#991b1b', color: '#fff', fontSize: '14px', padding: '16px', fontWeight: 'bold', cursor: 'pointer' },
      });
    } finally {
      setDeleteLoading(false);
      setDeleteModalOpen(false);
      setDeleteConfirmText('');
    }
  };

  const handleCancelUpload = async (sourceDocId: string) => {
    if (!companyId) return;
    setCancellingUpload(sourceDocId);
    try {
      const cancelUpload = httpsCallable(functions, 'cancelUploadProcessing');
      await cancelUpload({ companyId, sourceDocId });
      toast.success('Upload processing cancelled');
      void logAction({
        action: 'ADMIN_UPLOAD_CANCELLED',
        companyId,
        resource: `companies/${companyId}/commission_sources/${sourceDocId}`,
        resourceName: sourceDocId,
        metadata: { sourceDocId }
      });
    } catch (err: any) {
      toast.error(`Failed to cancel: ${err.message || 'Unknown error'}`, {
        duration: 8000,
        style: { background: '#991b1b', color: '#fff', fontSize: '14px', padding: '16px', fontWeight: 'bold', cursor: 'pointer' },
      });
    } finally {
      setCancellingUpload(null);
    }
  };

  // Helper functions
  const getStatusClass = (status?: string) => {
    const classes: { [key: string]: string } = {
      active: 'bg-green-500/20 text-green-400 border-green-500/30',
      inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      queued: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      completed: 'bg-green-500/20 text-green-400 border-green-500/30',
      completed_with_errors: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      completed_with_rejections: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      rejected_duplicate: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      failed: 'bg-red-500/20 text-red-400 border-red-500/30',
      cancelled: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    };
    return classes[status?.toLowerCase() || 'inactive'] || classes.inactive;
  };

  const getRoleDisplayName = (role: string) => {
    const roles: { [key: string]: string } = {
      super_admin: 'Super Admin', administrator: 'Administrator', office_admin: 'Office Admin',
      broker: 'Broker', external: 'External User'
    };
    return roles[role] || role;
  };

  const currentTheme = themes[activeTheme];
  const gradientStyle = {
    background: `linear-gradient(135deg, ${currentTheme.from}, ${currentTheme.to})`
  };

  // Calculate total files across all ZIPs
  const totalFiles = zipUploads.reduce((sum, zip) => sum + (zip.filesQueued || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-slate-700 rounded-full"></div>
            <div
              className="absolute top-0 w-20 h-20 border-4 border-transparent rounded-full animate-spin"
              style={{ borderTopColor: currentTheme.from }}
            />
          </div>
          <p className="text-slate-400 mt-4">Loading company details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center bg-red-500/10 border border-red-500/30 rounded-lg p-6 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/companies')}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  if (!company) return null;

  const displayName = company.companyName || company.name || 'Unnamed Company';

  return (
    <>
      {/* Page Header */}
      <div
        className="rounded-2xl p-8 mb-6 shadow-2xl relative overflow-hidden"
        style={gradientStyle}
      >
        <div className="absolute inset-0 opacity-30">
          <div className="absolute -top-4 -right-4 w-40 h-40 bg-white/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse delay-700" />
        </div>
        <div className="relative">
          <Link
            to="/companies"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Companies
          </Link>
          <h2 className="text-4xl font-bold text-white">{displayName}</h2>
          <p className="text-white/80 mt-2">Complete company management & analytics dashboard</p>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Subscription', value: subscriptionConfig?.tier ? `${subscriptionConfig.tier.charAt(0).toUpperCase() + subscriptionConfig.tier.slice(1)} (${subscriptionConfig.billing?.period || ''})` : 'N/A', icon: TrendingUp, detail: subscriptionConfig?.state || '' },
          { label: 'Active Brokers', value: brokers.filter(b => b.status === 'active').length, icon: Users, detail: `${brokers.length} total` },
          { label: 'Uploads', value: clientUploads.length || zipUploads.length, icon: Package, detail: `${zipUploads.length} admin ZIP${zipUploads.length !== 1 ? 's' : ''}` },
          { label: 'Active Users', value: activeUserCount, icon: Activity, detail: '' }
        ].map((stat, i) => (
          <div
            key={i}
            className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-slate-600/50 transition-all"
          >
            <div className="flex items-center justify-between mb-2">
              <stat.icon className="w-5 h-5 text-slate-400" />
              {stat.detail && <span className="text-xs text-slate-500">{stat.detail}</span>}
            </div>
            <p className="text-2xl font-bold text-white">{stat.value}</p>
            <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Action Bar */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 mb-6 flex justify-between items-center border border-slate-700/50">
        <div className="flex items-center gap-6">
          <span className="text-slate-400">ACL: <span className="text-white font-mono">{company.acl || 'N/A'}</span></span>
          <span className="text-slate-400">Legal name: <span className="text-white">{company.legalName || 'N/A'}</span></span>
          <span className="text-slate-400">ABN: <span className="text-white font-mono">{company.abn || 'N/A'}</span></span>
          <span className={`px-3 py-1 rounded-full text-sm border ${getStatusClass(company.status)}`}>
            {company.status || 'inactive'}
          </span>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/subscribers/${companyId}/verify`}
            className="px-4 py-2 bg-blue-600/20 text-blue-300 border border-blue-500/30 rounded-lg hover:bg-blue-600/30 transition-all hover:scale-105 flex items-center gap-2"
            title="Verify subscriber dashboards (read-only, audit-logged)"
          >
            <ShieldCheck className="w-4 h-4" />
            Verify subscriber
          </Link>
          <button
            onClick={() => setEditModalOpen(true)}
            className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-all hover:scale-105 flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" />
            Edit Company
          </button>
          <button
            onClick={() => setAccessModalOpen(true)}
            className="px-4 py-2 text-white rounded-lg transition-all hover:scale-105 hover:shadow-lg flex items-center gap-2"
            style={{ ...gradientStyle, boxShadow: `0 4px 15px ${currentTheme.from}40` }}
          >
            <Users className="w-4 h-4" />
            Manage Access
          </button>
          <button
            onClick={handleArchiveCompany}
            className="px-4 py-2 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-lg hover:bg-orange-500/30 transition-all hover:scale-105 flex items-center gap-2"
          >
            <Archive className="w-4 h-4" />
            Archive
          </button>
          <button
            onClick={handleDeleteCompany}
            className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/30 transition-all hover:scale-105 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl w-fit border border-slate-700/50">
        {['overview', 'files', 'brokers', 'offices', 'verification', 'upload-report', 'email-upload', ...(entorsTemplateId ? ['entors'] : []), ...(pipelineTrackerGranted ? ['pipeline-tracker'] : [])].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab as any)}
            className={`px-6 py-3 rounded-lg font-medium transition-all capitalize ${
              activeTab === tab
                ? 'text-white shadow-lg'
                : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
            }`}
            style={activeTab === tab ? {
              ...gradientStyle,
              boxShadow: `0 4px 15px ${currentTheme.from}30`
            } : {}}
          >
            {tab === 'entors' ? 'Entors Upload' : tab === 'upload-report' ? 'Upload Report' : tab === 'email-upload' ? 'Email Upload' : tab === 'pipeline-tracker' ? 'Pipeline Tracker' : tab} {tab === 'files' && `(${clientUploads.length || zipUploads.length})`} {tab === 'brokers' && `(${brokers.length})`} {tab === 'offices' && `(${offices.length})`}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* CRITICAL: Real-Time Upload Monitor - ALWAYS VISIBLE AT TOP */}
          {activeZipUpload && (
            <div className="animate-pulse-slow">
              <ZipUploadMonitor
                companyId={activeZipUpload.companyId}
                sourceDocId={activeZipUpload.sourceDocId}
                onComplete={() => {
                  setTimeout(() => setActiveZipUpload(null), 5000);
                }}
                onViewVerification={() => setActiveTab('verification')}
              />
            </div>
          )}

          <div className="mb-6">
            <AggregatorProfileCard
              companyId={company.id}
              aggregator={company.aggregator}
              featureFlags={company.featureFlags}
              onSaved={({ aggregator, flags }) =>
                setCompany((prev) =>
                  prev ? { ...prev, aggregator, featureFlags: { ...(prev.featureFlags ?? {}), ...flags } } : prev,
                )
              }
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: currentTheme.from }} />
              Company Information
            </h3>
            <div className="grid grid-cols-2 gap-6">
              {[
                { label: 'Email', value: company.email || 'Not provided', icon: '📧' },
                { label: 'Phone', value: company.phone || 'Not provided', icon: '📱' },
                { label: 'State', value: company.state || 'Not provided', icon: '📍' },
                { label: 'Address', value: company.address || 'Not provided', icon: '🏢' },
                { label: 'Active Brokers', value: company.brokerCount || 0, icon: '👥' },
                { label: 'Monthly Revenue', value: `$${company.monthlyRevenue?.toLocaleString() || 0}`, icon: '💰' }
              ].map((item, i) => (
                <div key={i} className="group">
                  <p className="text-slate-400 text-sm mb-1 flex items-center gap-1">
                    <span>{item.icon}</span>
                    {item.label}
                  </p>
                  <p className="text-white text-lg group-hover:text-blue-400 transition-colors">
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="flex items-center justify-between mb-2">
                <Package className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-slate-500">Total</span>
              </div>
              <h4 className="text-slate-400 text-sm mb-2">ZIP Uploads</h4>
              <p
                className="text-4xl font-bold"
                style={{
                  background: `linear-gradient(135deg, ${currentTheme.from}, ${currentTheme.to})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {zipUploads.length}
              </p>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-green-400">+12%</span>
              </div>
              <h4 className="text-slate-400 text-sm mb-2">Active Users</h4>
              <p
                className="text-4xl font-bold"
                style={{
                  background: `linear-gradient(135deg, ${currentTheme.from}, ${currentTheme.to})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {company.activeUsers || 0}
              </p>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Files Tab - REDESIGNED FOR BETTER UX */}
      {activeTab === 'files' && (
        <div className="space-y-6">
          {/* Upload Section - Full Width at Top */}
          {companyId && (
            <OnboardingDataUploader
              companyId={companyId}
              onUploadSuccess={(result) => {
                if (result?.sourceDocId) {
                  setActiveZipUpload({
                    companyId,
                    sourceDocId: result.sourceDocId
                  });
                }
              }}
            />
          )}

          {/* Client Processed Uploads */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
            <button
              type="button"
              onClick={() => setProcessedUploadsExpanded(prev => !prev)}
              aria-expanded={processedUploadsExpanded}
              className="w-full p-6 border-b border-slate-700/50 flex items-center justify-between text-left hover:bg-slate-700/20 transition-colors"
            >
              <div>
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <File className="w-5 h-5" style={{ color: currentTheme.from }} />
                  Processed Uploads
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  {clientUploads.length} file{clientUploads.length !== 1 ? 's' : ''} from client processing queue
                </p>
              </div>
              {processedUploadsExpanded ? (
                <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              )}
            </button>
            {processedUploadsExpanded && (
            <div className="divide-y divide-slate-700/50">
              {clientUploadsLoading ? (
                <div className="p-8 text-center">
                  <Loader className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2" />
                  <p className="text-slate-500">Loading uploads...</p>
                </div>
              ) : clientUploads.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                        <th className="px-6 py-3">File Name</th>
                        <th className="px-6 py-3">Entity</th>
                        <th className="px-6 py-3">Period</th>
                        <th className="px-6 py-3">Run</th>
                        <th className="px-6 py-3 text-right">Records</th>
                        <th className="px-6 py-3 text-right">Commission</th>
                        <th className="px-6 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/30">
                      {clientUploads.map((upload: any) => (
                        <tr key={upload.queueDocId} className="hover:bg-slate-700/20 transition-colors">
                          <td className="px-6 py-3 text-sm text-white font-mono truncate max-w-[200px]" title={upload.fileName}>
                            {upload.fileName}
                          </td>
                          <td className="px-6 py-3 text-sm text-slate-300">{upload.entityCode || '—'}</td>
                          <td className="px-6 py-3 text-sm text-slate-300">{upload.periodMonth}</td>
                          <td className="px-6 py-3 text-sm text-slate-300">R{upload.runNumber}</td>
                          <td className="px-6 py-3 text-sm text-slate-300 text-right">{upload.recordCount?.toLocaleString()}</td>
                          <td className="px-6 py-3 text-sm text-slate-300 text-right">
                            {upload.totalCommissionCents != null
                              ? `$${(upload.totalCommissionCents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : '—'}
                          </td>
                          <td className="px-6 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs border ${getStatusClass(upload.processingStatus)}`}>
                              {upload.processingStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center text-slate-500">No client uploads found</div>
              )}
            </div>
            )}
          </div>

          {/* Admin Uploaded ZIP Files */}
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
            <button
              type="button"
              onClick={() => setAdminZipsExpanded(prev => !prev)}
              aria-expanded={adminZipsExpanded}
              className="w-full p-6 border-b border-slate-700/50 flex items-center justify-between text-left hover:bg-slate-700/20 transition-colors"
            >
              <div>
                <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Package className="w-5 h-5" style={{ color: currentTheme.from }} />
                  Admin ZIP Uploads
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  {zipUploads.length} ZIP file{zipUploads.length !== 1 ? 's' : ''} containing {totalFiles} individual file{totalFiles !== 1 ? 's' : ''}
                </p>
              </div>
              {adminZipsExpanded ? (
                <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              )}
            </button>
            {adminZipsExpanded && (
              <div className="divide-y divide-slate-700/50">
                {zipUploads.length > 0 ? (
                  zipUploads.map((zip) => {
                    const isExpanded = expandedZips.has(zip.id);
                    const processingFiles = zip.processing ? Object.entries(zip.processing) : [];

                    return (
                      <div key={zip.id} className="hover:bg-slate-800/30 transition-colors">
                        {/* ZIP Header */}
                        <div
                          className="p-4 cursor-pointer"
                          onClick={() => toggleZipExpansion(zip.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center"
                                style={{
                                  background: `linear-gradient(135deg, ${currentTheme.from}20, ${currentTheme.to}20)`
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-5 h-5" style={{ color: currentTheme.from }} />
                                ) : (
                                  <ChevronRight className="w-5 h-5" style={{ color: currentTheme.from }} />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4 text-slate-400" />
                                  <p className="font-medium text-white">
                                    {zip.displayName || formatUploadDisplayName(zip.fileName)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-4 mt-1">
                                  <p className="text-sm text-slate-400">
                                    {zip.uploadedAt ? zip.uploadedAt.toDate().toLocaleString() : 'N/A'}
                                  </p>
                                  <p className="text-sm text-slate-500">
                                    {zip.filesQueued || 0} files
                                  </p>
                                  {zip.filesFailed && zip.filesFailed > 0 && (
                                    <p className="text-sm text-red-400">
                                      {zip.filesFailed} failed
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusClass(zip.status)}`}>
                                {zip.status}
                              </span>
                              {(zip.status === 'processing' || zip.status === 'queued' || zip.status === 'failed') && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleCancelUpload(zip.id); }}
                                  disabled={cancellingUpload === zip.id}
                                  className="px-3 py-1 text-xs font-medium rounded-full bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 disabled:opacity-50 flex items-center gap-1 transition-colors"
                                >
                                  {cancellingUpload === zip.id ? (
                                    <><Loader className="w-3 h-3 animate-spin" />Cancelling...</>
                                  ) : (
                                    <><X className="w-3 h-3" />Stop</>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Expanded ZIP Contents - rendered from embedded processing map */}
                        {isExpanded && (
                          <div className="bg-slate-900/50 border-t border-slate-700/50">
                            {processingFiles.length > 0 ? (
                              <div className="divide-y divide-slate-700/30">
                                {processingFiles
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([fileName, fileStatus]) => (
                                  <div key={fileName} className="p-3 pl-16 hover:bg-slate-800/20 transition-colors">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3 flex-1">
                                        {/* Status icon */}
                                        {fileStatus.status === 'completed' && (
                                          <div className="w-4 h-4 text-green-400 flex items-center justify-center">✓</div>
                                        )}
                                        {fileStatus.status === 'failed' && (
                                          <div className="w-4 h-4 text-red-400 flex items-center justify-center">✗</div>
                                        )}
                                        {fileStatus.status === 'processing' && (
                                          <Loader className="w-4 h-4 text-blue-400 animate-spin" />
                                        )}
                                        {fileStatus.status === 'queued' && (
                                          <div className="w-4 h-4 text-yellow-400 flex items-center justify-center">○</div>
                                        )}
                                        {fileStatus.status === 'cancelled' && (
                                          <div className="w-4 h-4 text-orange-400 flex items-center justify-center">⊘</div>
                                        )}
                                        {fileStatus.status === 'rejected_duplicate' && (
                                          <div className="w-4 h-4 text-amber-400 flex items-center justify-center">⊘</div>
                                        )}
                                        <div className="flex-1">
                                          <p className="text-sm text-white">{fileName}</p>
                                          <div className="flex items-center gap-3 mt-1">
                                            {fileStatus.recordsWritten !== undefined && (
                                              <p className="text-xs text-slate-500">
                                                {fileStatus.recordsWritten} records
                                              </p>
                                            )}
                                            {fileStatus.totalRows !== undefined && fileStatus.totalRows > 0 && (
                                              <p className="text-xs text-slate-500">
                                                ({fileStatus.totalRows} total rows)
                                              </p>
                                            )}
                                            {fileStatus.skippedDuplicates !== undefined && fileStatus.skippedDuplicates > 0 && (
                                              <p className="text-xs text-yellow-500">
                                                {fileStatus.skippedDuplicates} duplicates
                                              </p>
                                            )}
                                            {fileStatus.successRate !== undefined && fileStatus.status === 'completed' && (
                                              <p className="text-xs text-green-500">
                                                {fileStatus.successRate}% success
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border whitespace-nowrap ${getStatusClass(fileStatus.status)}`}>
                                        {fileStatus.status === 'rejected_duplicate' ? 'rejected (duplicate)' : fileStatus.status}
                                      </span>
                                    </div>
                                    {fileStatus.error && (
                                      <div className="mt-2 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                                        {fileStatus.error}
                                      </div>
                                    )}
                                    {fileStatus.status === 'rejected_duplicate' && (
                                      <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
                                        Duplicate of an already-uploaded file — not re-imported
                                        {fileStatus.detectedPeriodMonth
                                          ? ` (${fileStatus.detectedPeriodMonth}${fileStatus.detectedRunNumber ? ` Run ${fileStatus.detectedRunNumber}` : ''})`
                                          : ''}
                                        {fileStatus.supersedesUploadId ? `. Already ingested as ${fileStatus.supersedesUploadId}.` : '.'}
                                      </div>
                                    )}
                                    {fileStatus.hasWarning && !fileStatus.error && (
                                      <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-400">
                                        Data loss warning - some records may have been skipped
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="p-8 text-center">
                                <p className="text-sm text-slate-400">No file processing data available</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  Processing data may not be available for older uploads
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="p-12 text-center">
                    <Package className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No ZIP files uploaded yet</p>
                    <p className="text-sm text-slate-500 mt-2">Upload a ZIP file containing commission data</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verification Tab */}
      {activeTab === 'verification' && companyId && (
        <RctiVerificationTab
          companyId={companyId}
          gradientStyle={gradientStyle}
          currentTheme={currentTheme}
        />
      )}

      {/* Upload Report Tab */}
      {activeTab === 'upload-report' && companyId && (
        <UploadVerificationReport companyId={companyId} />
      )}

      {/* Entors Upload Tab */}
      {activeTab === 'entors' && entorsTemplateId && (
        <div className="space-y-6">
          <EntorsUploadSection
            companyId={companyId!}
            offices={offices}
            entorsTemplateId={entorsTemplateId}
            onUploadSuccess={(result) => {
              if (result?.sourceDocId) {
                setActiveZipUpload({
                  companyId: companyId!,
                  sourceDocId: result.sourceDocId
                });
              }
            }}
          />

          {/* Show active upload monitor if processing */}
          {activeZipUpload && (
            <div className="animate-pulse-slow">
              <ZipUploadMonitor
                companyId={activeZipUpload.companyId}
                sourceDocId={activeZipUpload.sourceDocId}
                onComplete={() => {
                  setTimeout(() => setActiveZipUpload(null), 5000);
                }}
                onViewVerification={() => setActiveTab('verification')}
              />
            </div>
          )}
        </div>
      )}

      {/* Email Upload Tab */}
      {activeTab === 'email-upload' && (
        <EmlUploadSection
          companyId={companyId!}
          offices={offices}
          onUploadSuccess={() => {
            // Optionally refresh data after upload
          }}
        />
      )}

      {/* Pipeline Tracker Tab */}
      {activeTab === 'pipeline-tracker' && (
        <PipelineTrackerUploadSection companyId={companyId!} />
      )}

      {/* Brokers Tab */}
      {activeTab === 'brokers' && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-6 border-b border-slate-700/50 flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5" style={{ color: currentTheme.from }} />
              Company Brokers
            </h3>
            <button
              onClick={() => setAccessModalOpen(true)}
              className="px-3 py-1.5 text-sm text-white rounded-lg transition-all hover:scale-105"
              style={gradientStyle}
            >
              Add Broker
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700/50">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {brokers.length > 0 ? (
                  brokers.map((broker) => (
                    <tr key={broker.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium">
                            {broker.displayName?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <p className="ml-3 text-sm font-medium text-white">{broker.displayName || 'N/A'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-slate-300">{broker.email}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-slate-300">{getRoleDisplayName(broker.role)}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusClass(broker.status || 'active')}`}>
                          {broker.status || 'active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-slate-400">
                          {broker.lastActive ? new Date(broker.lastActive.seconds * 1000).toLocaleDateString() : 'Never'}
                        </p>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">No brokers registered for this company</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Offices Tab - Principal Broker Management */}
      {activeTab === 'offices' && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-6 border-b border-slate-700/50">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: currentTheme.from }} />
              Offices & Principal Brokers
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Assign a principal broker to each office. This broker will receive commission attribution when source files have empty broker names.
            </p>
          </div>
          <div className="divide-y divide-slate-700/50">
            {offices.length > 0 ? (
              offices.map((office) => {
                const principalBroker = brokers.find(b => b.id === office.principalBrokerId);
                return (
                  <div key={office.id} className="p-4 hover:bg-slate-800/30 transition-colors">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-white font-medium">{office.name}</h4>
                        {principalBroker && (
                          <p className="text-sm text-slate-400 mt-1">
                            Principal: <span className="text-green-400">{principalBroker.displayName}</span>
                          </p>
                        )}
                      </div>
                      <select
                        value={office.principalBrokerId || ''}
                        onChange={async (e) => {
                          const newPrincipalBrokerId = e.target.value || null;
                          try {
                            // Update Firestore directly
                            await updateDoc(doc(db, `companies/${companyId}/offices/${office.id}`), {
                              principalBrokerId: newPrincipalBrokerId,
                              updatedAt: serverTimestamp()
                            });
                            // Update local state
                            setOffices(prev => prev.map(o =>
                              o.id === office.id ? { ...o, principalBrokerId: newPrincipalBrokerId } : o
                            ));
                            toast.success(`Principal broker updated for ${office.name}`);
                            // Audit log
                            await logAction({
                              action: 'PRINCIPAL_BROKER_UPDATED',
                              companyId: companyId!,
                              resource: `companies/${companyId}/offices/${office.id}`,
                              resourceName: office.name,
                              metadata: {
                                officeName: office.name,
                                previousPrincipalBrokerId: office.principalBrokerId,
                                newPrincipalBrokerId,
                                newPrincipalBrokerName: brokers.find(b => b.id === newPrincipalBrokerId)?.displayName || null
                              }
                            });
                          } catch (err) {
                            console.error('Error updating principal broker:', err);
                            toast.error('Failed to update principal broker');
                          }
                        }}
                        className="bg-slate-700 text-white rounded-lg px-4 py-2 border border-slate-600 focus:border-blue-500 focus:outline-none min-w-[200px]"
                      >
                        <option value="">-- Select Principal Broker --</option>
                        {[...brokers].sort((a, b) => a.displayName.localeCompare(b.displayName)).map(broker => (
                          <option key={broker.id} value={broker.id}>
                            {broker.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-12 text-center">
                <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No offices configured for this company</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODALS - Edit Company */}
      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditModalOpen(false)} />
          <div className="relative bg-slate-800/95 backdrop-blur-xl rounded-2xl w-full max-w-lg border border-slate-700/50 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Edit Company Details</h3>
                <button onClick={() => setEditModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Company Name</label>
                  <input
                    type="text"
                    value={companyData.companyName}
                    onChange={(e) => setCompanyData({...companyData, companyName: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Legal Name</label>
                  <input
                    type="text"
                    value={companyData.legalName}
                    onChange={(e) => setCompanyData({...companyData, legalName: e.target.value})}
                    placeholder="Registered entity name, e.g. Example Finance Pty Ltd"
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  />
                  <p className="mt-1 text-xs text-slate-500">Shown in the footer of the tenant&apos;s marketing emails. Required before they can be sent.</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">ABN</label>
                    <input
                      type="text"
                      value={companyData.abn}
                      onChange={(e) => setCompanyData({...companyData, abn: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">ACL</label>
                    <input
                      type="text"
                      value={companyData.acl}
                      onChange={(e) => setCompanyData({...companyData, acl: e.target.value})}
                      className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Email</label>
                  <input
                    type="email"
                    value={companyData.email}
                    onChange={(e) => setCompanyData({...companyData, email: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={companyData.phone}
                    onChange={(e) => setCompanyData({...companyData, phone: e.target.value})}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setEditModalOpen(false)}
                  className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCompany}
                  disabled={saveLoading}
                  className="px-4 py-2 text-white rounded-lg hover:scale-105 transition-all disabled:opacity-50"
                  style={{ ...gradientStyle }}
                >
                  {saveLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Access Modal */}
      {accessModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAccessModalOpen(false)} />
          <div className="relative bg-slate-800/95 backdrop-blur-xl rounded-2xl w-full max-w-2xl border border-slate-700/50 shadow-2xl">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-white">Manage Access</h3>
                <button onClick={() => setAccessModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-white mb-3">Invite New User</h4>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={inviteData.email}
                    onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                    placeholder="Enter email"
                    className="flex-1 px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  />
                  <select
                    value={inviteData.role}
                    onChange={(e) => setInviteData({...inviteData, role: e.target.value})}
                    className="px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                  >
                    <option value="broker">Broker</option>
                    <option value="office_admin">Office Admin</option>
                    <option value="administrator">Administrator</option>
                  </select>
                  <button
                    onClick={handleSendInvitation}
                    disabled={inviteLoading || !inviteData.email}
                    className="px-4 py-2 text-white rounded-lg disabled:opacity-50"
                    style={gradientStyle}
                  >
                    {inviteLoading ? 'Sending...' : 'Send Invite'}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button onClick={() => setAccessModalOpen(false)} className="px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !deleteLoading && setDeleteModalOpen(false)} />
          <div className="relative bg-slate-800/95 backdrop-blur-xl rounded-2xl w-full max-w-lg border border-slate-700/50 shadow-2xl">
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className={`p-3 rounded-lg ${deleteMode === 'delete' ? 'bg-red-500/20' : 'bg-orange-500/20'}`}>
                  <AlertCircle className={`w-6 h-6 ${deleteMode === 'delete' ? 'text-red-400' : 'text-orange-400'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {deleteMode === 'delete' ? 'Permanently Delete Company?' : 'Archive Company?'}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {deleteMode === 'delete'
                      ? 'This action cannot be undone. All company data will be permanently deleted.'
                      : 'The company will be archived and can be restored later if needed.'}
                  </p>
                </div>
                <button onClick={() => setDeleteModalOpen(false)} disabled={deleteLoading} className="p-1 rounded hover:bg-slate-700 transition-colors">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="mb-6 p-4 bg-slate-700/30 rounded-lg">
                <div className="text-sm text-slate-300 mb-2">
                  <strong>Company:</strong> {displayName}
                </div>
                <div className="text-sm text-yellow-400 flex items-center gap-2 mt-2">
                  <Users className="w-4 h-4" />
                  {brokers.length} users will be affected
                </div>
              </div>
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deleteUsers}
                    onChange={(e) => setDeleteUsers(e.target.checked)}
                    disabled={deleteLoading}
                    className="w-4 h-4 rounded"
                  />
                  {deleteMode === 'delete' ? 'Also delete all user accounts' : 'Also deactivate all user accounts'}
                </label>
              </div>
              {deleteMode === 'delete' && (
                <div className="mb-6">
                  <label className="block text-sm text-slate-400 mb-2">
                    Type <span className="font-mono font-bold text-white">DELETE</span> to confirm:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    disabled={deleteLoading}
                    className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    placeholder="DELETE"
                  />
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAction}
                  disabled={deleteLoading || (deleteMode === 'delete' && deleteConfirmText !== 'DELETE')}
                  className={`flex-1 px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                    deleteMode === 'delete' ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                >
                  {deleteLoading ? 'Processing...' : (deleteMode === 'delete' ? 'Delete Permanently' : 'Archive')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyDetailsPage;
